import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Clock, Square, Loader2,
  CheckCircle, AlertTriangle, Star, TrendingUp,
  MessageSquare, ThumbsUp, ThumbsDown, Minus, ChevronDown,
  Mic, MicOff, Volume2, VolumeX, Download, FileText, Play, StopCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LLMHealthBanner from '../components/LLMHealthBanner';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_AVATAR = "https://images.unsplash.com/photo-1739288717689-1687e8133358?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZ3JlZW4lMjBhaSUyMGdsb3dpbmclMjBvcmIlMjBsb2dvfGVufDB8fHx8MTc3NTU0NTk2N3ww&ixlib=rb-4.1.0&q=85&w=80&h=80";

const MockInterviewSessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Voice mode state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  const token = localStorage.getItem('codearena_token');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!token) { navigate(loginUrlWithRedirect('/codearena/auth'), { replace: true }); return; }
    fetchSession();
  }, [sessionId, token]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Timer
  useEffect(() => {
    if (!session || session.status !== 'active' || timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleEndInterview();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [session?.status, timeLeft]);

  const fetchSession = async () => {
    try {
      const res = await axios.get(`${API}/mock-interview/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(res.data.session);
      setMessages(res.data.messages || []);

      if (res.data.session.status === 'active') {
        const created = new Date(res.data.session.created_at).getTime();
        const duration = (res.data.session.duration_minutes || 10) * 60 * 1000;
        const remaining = Math.max(0, Math.floor((created + duration - Date.now()) / 1000));
        setTimeLeft(remaining);
      }

      if (res.data.session.status === 'completed') {
        setShowFeedback(true);
      }
    } catch (err) {
      toast.error('Failed to load interview session');
      navigate('/codearena/mock-interview');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic user message
    const tempUserMsg = { id: 'temp-user', role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await axios.post(`${API}/mock-interview/sessions/${sessionId}/message`,
        { message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Replace temp message and add AI response
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-user'),
        res.data.user_message,
        res.data.ai_message
      ]);
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      toast.error('Failed to send message. Try again.');
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleEndInterview = async () => {
    if (ending) return;
    setEnding(true);
    clearInterval(timerRef.current);

    try {
      const res = await axios.post(`${API}/mock-interview/sessions/${sessionId}/end`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSession(res.data.session);
      setShowFeedback(true);
      toast.success('Interview completed! Generating feedback...');
    } catch (err) {
      toast.error('Failed to end interview');
    } finally {
      setEnding(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') return;
    clearInterval(recordingTimerRef.current);
    setIsRecording(false);

    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(blob);
      };
      mediaRecorderRef.current.stop();
    });
  };

  const sendVoiceMessage = async () => {
    const audioBlob = await stopRecording();
    if (!audioBlob || audioBlob.size < 100) {
      toast.error('Recording too short. Please try again.');
      return;
    }
    setSending(true);
    // Optimistic "recording sent" indicator
    const tempMsg = { id: 'temp-voice', role: 'user', content: 'Transcribing your voice...', is_voice: true, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      const res = await axios.post(`${API}/mock-interview/sessions/${sessionId}/voice-message`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-voice'),
        res.data.user_message,
        res.data.ai_message
      ]);
      // Auto-play AI audio response
      if (res.data.ai_message?.audio_base64) {
        playAudioBase64(res.data.ai_message.audio_base64, res.data.ai_message.id);
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== 'temp-voice'));
      toast.error(err.response?.data?.detail || 'Failed to process voice message');
    } finally {
      setSending(false);
    }
  };

  const playAudioBase64 = (base64, msgId) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }
    const audio = new Audio(`data:audio/mp3;base64,${base64}`);
    audioPlayerRef.current = audio;
    setPlayingAudioId(msgId);
    audio.play();
    audio.onended = () => { setPlayingAudioId(null); audioPlayerRef.current = null; };
    audio.onerror = () => { setPlayingAudioId(null); audioPlayerRef.current = null; };
  };

  const requestTTS = async (text, msgId) => {
    setTtsLoading(msgId);
    try {
      const res = await axios.post(`${API}/mock-interview/sessions/${sessionId}/tts`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.audio_base64) {
        playAudioBase64(res.data.audio_base64, msgId);
      }
    } catch {
      toast.error('Failed to generate audio');
    } finally {
      setTtsLoading(null);
    }
  };

  const downloadPdfReport = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/mock-interview/sessions/${sessionId}/report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `interview_report_${session?.job_title?.replace(/\s/g, '_') || 'report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0714' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-white/60 font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Loading interview...</span>
        </div>
      </div>
    );
  }

  if (!session) return null;

  const feedback = session.feedback;
  const isActive = session.status === 'active';
  const isTimeLow = timeLeft > 0 && timeLeft < 60;
  const isVoiceMode = session.mode === 'voice';

  // Feedback View
  if (showFeedback && feedback) {
    return (
      <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top center, #2A1B44 0%, #0B0714 50%, #0B0714 100%)', fontFamily: 'Outfit, sans-serif' }}>
        <div className="max-w-3xl mx-auto p-4 sm:p-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <button onClick={() => navigate('/codearena/mock-interview')} data-testid="feedback-back-btn"
              className="w-10 h-10 rounded-full flex items-center justify-center border transition-all hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
              <ArrowLeft size={18} className="text-white" />
            </button>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Interview Report</h2>
              <p className="text-white/40 text-sm">{session.job_title} &middot; {session.interview_type}</p>
            </div>
          </div>

          {/* Overall Score */}
          <div className="bg-white rounded-2xl p-8 mb-6 text-center" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
              style={{ background: `conic-gradient(#00E59B ${(feedback.overall_score / 100) * 360}deg, #E4E4E7 0deg)` }}>
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
                <span className="text-3xl font-black text-zinc-900" data-testid="overall-score">{feedback.overall_score}</span>
              </div>
            </div>
            <p className="text-sm font-bold text-zinc-400 uppercase tracking-wider">Overall Score</p>
          </div>

          {/* Score Breakdown */}
          <div className="bg-white rounded-2xl p-6 mb-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <h3 className="text-lg font-bold text-zinc-900 mb-5">Score Breakdown</h3>
            <div className="space-y-4">
              {Object.entries(feedback.scores || {}).map(([key, value]) => (
                <div key={key} data-testid={`score-${key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-zinc-700 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-bold" style={{ color: value >= 70 ? '#00E59B' : value >= 40 ? '#F5A623' : '#FF3B30' }}>
                      {value}%
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000"
                      style={{ width: `${value}%`, background: value >= 70 ? '#00E59B' : value >= 40 ? '#F5A623' : '#FF3B30' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths & Improvements */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <h3 className="flex items-center gap-2 text-base font-bold text-zinc-900 mb-4">
                <ThumbsUp size={18} className="text-emerald-500" /> Strengths
              </h3>
              <ul className="space-y-2.5">
                {(feedback.strengths || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-600" data-testid={`strength-${i}`}>
                    <CheckCircle size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <h3 className="flex items-center gap-2 text-base font-bold text-zinc-900 mb-4">
                <TrendingUp size={18} className="text-amber-500" /> Improve
              </h3>
              <ul className="space-y-2.5">
                {(feedback.improvements || []).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-600" data-testid={`improvement-${i}`}>
                    <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Detailed Feedback */}
          <div className="bg-white rounded-2xl p-6 mb-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <h3 className="text-lg font-bold text-zinc-900 mb-3">Detailed Feedback</h3>
            <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap" data-testid="detailed-feedback">
              {feedback.detailed_feedback}
            </p>
          </div>

          {/* Question-by-Question */}
          {feedback.question_feedback && feedback.question_feedback.length > 0 && (
            <div className="bg-white rounded-2xl p-6 mb-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <h3 className="text-lg font-bold text-zinc-900 mb-5">Question-by-Question Review</h3>
              <div className="space-y-5">
                {feedback.question_feedback.map((qf, i) => (
                  <div key={i} className="border border-zinc-100 rounded-xl p-4" data-testid={`question-feedback-${i}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <MessageSquare size={16} className="text-zinc-400 mt-0.5 shrink-0" />
                      <p className="text-sm font-semibold text-zinc-800">{qf.question}</p>
                    </div>
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          qf.rating === 'good' ? 'bg-emerald-50 text-emerald-700' :
                          qf.rating === 'average' ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          {qf.rating === 'good' ? 'Good' : qf.rating === 'average' ? 'Average' : 'Needs Work'}
                        </span>
                      </div>
                      {qf.candidate_answer_summary && (
                        <p className="text-xs text-zinc-500"><span className="font-semibold">Your answer:</span> {qf.candidate_answer_summary}</p>
                      )}
                      {qf.suggested_answer && (
                        <div className="bg-emerald-50 rounded-lg p-3 mt-1">
                          <p className="text-xs text-emerald-800"><span className="font-semibold">Suggested:</span> {qf.suggested_answer}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate('/codearena/mock-interview')} data-testid="back-to-dashboard-btn"
              className="flex-1 py-3.5 rounded-full font-bold text-sm border transition-all hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
              Back to Dashboard
            </button>
            <button onClick={downloadPdfReport} disabled={downloadingPdf} data-testid="download-pdf-btn"
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm border transition-all hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
              {downloadingPdf ? (
                <><Loader2 size={16} className="animate-spin" /> Generating PDF...</>
              ) : (
                <><Download size={16} /> Download PDF Report</>
              )}
            </button>
            <button onClick={() => { navigate('/codearena/mock-interview'); }} data-testid="try-again-btn"
              className="flex-1 py-3.5 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
              style={{ background: '#00E59B', color: '#09090B', boxShadow: '0 0 20px rgba(0,229,155,0.3)' }}>
              Try Another Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Chat View
  return (
    <div className="h-screen flex flex-col" style={{ background: '#0B0714', fontFamily: 'Outfit, sans-serif' }}>
      <LLMHealthBanner />
      {/* Top Bar */}
      <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-b"
        style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(40px)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/codearena/mock-interview')} data-testid="chat-back-btn"
            className="w-8 h-8 rounded-full flex items-center justify-center border transition-all hover:bg-white/10"
            style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            <ArrowLeft size={16} className="text-white" />
          </button>
          <div>
            <p className="text-sm font-semibold text-white">{session.job_title}</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-white/40">{session.interview_type.charAt(0).toUpperCase() + session.interview_type.slice(1)} Interview</p>
              {isVoiceMode && (
                <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,229,155,0.15)', color: '#00E59B' }}>
                  <Mic size={10} /> Voice
                </span>
              )}
              {session.resume_text && (
                <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(168,85,247,0.15)', color: '#A855F7' }}>
                  <FileText size={10} /> Resume
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {isActive && timeLeft > 0 && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isTimeLow ? 'animate-pulse' : ''}`}
              style={{ background: isTimeLow ? 'rgba(255,59,48,0.15)' : 'rgba(0,229,155,0.1)' }}
              data-testid="interview-timer">
              <Clock size={14} style={{ color: isTimeLow ? '#FF3B30' : '#00E59B' }} />
              <span className="font-mono text-sm font-bold tracking-widest"
                style={{ color: isTimeLow ? '#FF3B30' : '#00E59B' }}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
          {isActive && (
            <button onClick={handleEndInterview} disabled={ending} data-testid="end-interview-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all">
              <Square size={12} fill="currentColor" />
              {ending ? 'Ending...' : 'End Interview'}
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-4" data-testid="messages-container">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`message-${msg.id}`}>
            {msg.role === 'assistant' && (
              <img src={AI_AVATAR} alt="AI" className="w-8 h-8 rounded-full object-cover shrink-0 mt-1"
                style={{ boxShadow: '0 0 12px rgba(0,229,155,0.3)' }} />
            )}
            <div className={`max-w-[80%] sm:max-w-[70%]`}>
              <div className={`${
                msg.role === 'user'
                  ? 'rounded-2xl rounded-tr-sm p-4 font-medium'
                  : 'rounded-2xl rounded-tl-sm p-4'
              }`} style={
                msg.role === 'user'
                  ? { background: '#00E59B', color: '#09090B' }
                  : { background: 'rgba(255,255,255,0.08)', color: '#fff' }
              }>
                {msg.is_voice && msg.role === 'user' && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <Mic size={12} className="opacity-60" />
                    <span className="text-[10px] font-bold uppercase opacity-60">Voice</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
              {/* Audio playback for AI voice messages */}
              {msg.role === 'assistant' && isVoiceMode && (
                <div className="mt-1.5 flex items-center gap-2">
                  {msg.audio_base64 ? (
                    <button
                      onClick={() => playingAudioId === msg.id
                        ? (() => { audioPlayerRef.current?.pause(); setPlayingAudioId(null); })()
                        : playAudioBase64(msg.audio_base64, msg.id)
                      }
                      data-testid={`play-audio-${msg.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all hover:bg-white/10"
                      style={{ color: playingAudioId === msg.id ? '#00E59B' : 'rgba(255,255,255,0.5)' }}>
                      {playingAudioId === msg.id ? <><StopCircle size={12} /> Playing...</> : <><Play size={12} /> Play Audio</>}
                    </button>
                  ) : (
                    <button
                      onClick={() => requestTTS(msg.content, msg.id)}
                      disabled={ttsLoading === msg.id}
                      data-testid={`tts-${msg.id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all hover:bg-white/10"
                      style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {ttsLoading === msg.id ? <><Loader2 size={12} className="animate-spin" /> Generating...</> : <><Volume2 size={12} /> Listen</>}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex gap-3 justify-start" data-testid="typing-indicator">
            <img src={AI_AVATAR} alt="AI" className="w-8 h-8 rounded-full object-cover shrink-0 mt-1" />
            <div className="rounded-2xl rounded-tl-sm p-4" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isActive ? (
        <div className="shrink-0 p-4 sm:px-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {isVoiceMode ? (
            /* Voice Mode Input */
            <div className="max-w-3xl mx-auto flex flex-col items-center gap-3">
              {isRecording && (
                <div className="flex items-center gap-2 text-red-400 text-sm font-semibold animate-pulse">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </div>
              )}
              <div className="flex items-center gap-4">
                {!isRecording ? (
                  <button onClick={startRecording} disabled={sending} data-testid="start-recording-btn"
                    className="w-16 h-16 rounded-full flex items-center justify-center transition-all hover:scale-105 disabled:opacity-40"
                    style={{ background: '#00E59B', boxShadow: '0 0 24px rgba(0,229,155,0.4)' }}>
                    {sending ? <Loader2 size={24} className="animate-spin text-zinc-900" /> : <Mic size={24} className="text-zinc-900" />}
                  </button>
                ) : (
                  <button onClick={sendVoiceMessage} data-testid="stop-recording-btn"
                    className="w-16 h-16 rounded-full flex items-center justify-center bg-red-500 transition-all hover:scale-105 animate-pulse"
                    style={{ boxShadow: '0 0 24px rgba(239,68,68,0.4)' }}>
                    <StopCircle size={24} className="text-white" />
                  </button>
                )}
              </div>
              <p className="text-white/30 text-xs">
                {isRecording ? 'Tap to stop and send' : sending ? 'Processing your answer...' : 'Tap to start speaking'}
              </p>
              {/* Text fallback in voice mode */}
              <div className="w-full flex items-end gap-3 mt-2">
                <div className="flex-1 relative">
                  <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown} rows={1} data-testid="chat-input"
                    placeholder="Or type your answer..."
                    className="w-full bg-white/5 border text-white rounded-2xl px-4 py-3 pr-12 text-sm resize-none outline-none transition-all focus:border-emerald-400/50 placeholder:text-white/30"
                    style={{ borderColor: 'rgba(255,255,255,0.1)', maxHeight: '120px', minHeight: '48px' }}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }} />
                </div>
                <button onClick={sendMessage} disabled={!input.trim() || sending} data-testid="send-message-btn"
                  className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30 hover:-translate-y-0.5"
                  style={{ background: '#00E59B', boxShadow: '0 0 16px rgba(0,229,155,0.3)' }}>
                  {sending ? <Loader2 size={18} className="animate-spin text-zinc-900" /> : <Send size={18} className="text-zinc-900" />}
                </button>
              </div>
            </div>
          ) : (
            /* Text Mode Input */
            <div className="max-w-3xl mx-auto flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown} rows={1} data-testid="chat-input"
                  placeholder="Type your answer..."
                  className="w-full bg-white/5 border text-white rounded-2xl px-4 py-3 pr-12 text-sm resize-none outline-none transition-all focus:border-emerald-400/50 placeholder:text-white/30"
                  style={{ borderColor: 'rgba(255,255,255,0.1)', maxHeight: '120px', minHeight: '48px' }}
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }} />
              </div>
              <button onClick={sendMessage} disabled={!input.trim() || sending} data-testid="send-message-btn"
                className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30 hover:-translate-y-0.5"
                style={{ background: '#00E59B', boxShadow: '0 0 16px rgba(0,229,155,0.3)' }}>
                {sending ? <Loader2 size={18} className="animate-spin text-zinc-900" /> : <Send size={18} className="text-zinc-900" />}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="shrink-0 p-4 text-center border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <p className="text-white/40 text-sm">This interview has ended.</p>
          <button onClick={() => setShowFeedback(true)} data-testid="view-feedback-btn"
            className="mt-2 text-sm font-bold transition-all" style={{ color: '#00E59B' }}>
            View Feedback
          </button>
        </div>
      )}
    </div>
  );
};

export default MockInterviewSessionPage;
